import { qsParse } from 'Helpers/navigation';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { getAppRoutes } from 'Pages/App/App.constants';
import PrivateApp from 'Pages/App/PrivateApp';
import { CognitoResponseError } from 'Services/Api/Auth/Types';
import { Redirect } from 'react-router';
import { useStores } from 'Hooks/useStore';
import ResetPasswordFormSubmit from './ResetPasswordFormSubmit';
import { Notification } from 'Components';
import { useResetExpiredPassword } from '../hooks';
import {
	COGNITO_ERROR_MSG,
	COGNITO_ERROR_MSG_TO_DESCRIPTION_MAP
} from 'app-config';
import NewAuthLayout from '../NewLogin/layout/Layout';
import styles from './ResetPassword.module.scss';

interface ResetPasswordParams {
	login?: string;
}

function ResetPassword() {
	const history = useHistory();
	const { t } = useTranslation();
	const { authStore } = useStores();
	const appRoutes = getAppRoutes();
	const resetExpiredPassword = useResetExpiredPassword();

	const queryParams = qsParse(history.location.search) as ResetPasswordParams;
	const login = queryParams.login ?? '';

	// Guard: if no login in query params, there's nothing to show here —
	// send back to home so the user starts from the modal again.
	if (!login) {
		return <Redirect to={appRoutes.HOME} />;
	}

	const sendSecurityCode = async (login: string, cb?: () => void) => {
		try {
			await authStore.resetPassword({ login });
			Notification.success({
				description: t(
					'auth.resetPasswordSubmitForm.notifications.verificationCodeSent'
				)
			});
			cb?.();
		} catch (e) {
			const error = e as CognitoResponseError;

			if (error.code === 'UserNotFoundException') {
				Notification.error({
					description: t(
						'auth.resetPasswordSubmitForm.notifications.error.userNotFondException'
					)
				});
				return;
			}

			if (error.code === 'NotAuthorizedException') {
				if (error.message === COGNITO_ERROR_MSG.DISABLED) {
					Notification.error({
						description: t(
							COGNITO_ERROR_MSG_TO_DESCRIPTION_MAP[
								COGNITO_ERROR_MSG.DISABLED
							]
						)
					});
					return;
				}

				try {
					await resetExpiredPassword(login);
				} catch (e) {
					throw e;
				}
				return;
			}

			throw e;
		}
	};

	return (
		<PrivateApp>
			{(isAuthenticated) => (
				<>
					{isAuthenticated ? (
						<Redirect to={appRoutes.HOME} />
					) : (
						<NewAuthLayout variant="centered">
							<div className={styles.container}>
								<ResetPasswordFormSubmit
									login={login}
									sendSecurityCode={sendSecurityCode}
								/>
							</div>
						</NewAuthLayout>
					)}
				</>
			)}
		</PrivateApp>
	);
}

export default ResetPassword;
